import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LocationWhereInput, UserLocationInput } from "../../../gql/hooks";
import {
  SetActicityStatusParams,
  SetBrandIdsParams,
  SetClientTypeParams,
  SetIsOwnerParams,
  SetLaLigaParams,
  SetLastVisitDateParams,
  SetMapLocationParams,
  SetMissingProductsParams,
  SetExistingProductsParams,
  SetQueryVariablesParams,
  SetRentabilibarParams,
  SetSearchParams,
  SetSegmentsParams,
  SetSegmentTypesParams,
  SetUnderContractParams,
  SetUserLocationParams,
  SortType,
  PosFilters,
} from "./types";
import {
  MAX_VOLUME_RANGE,
  MIN_VOLUME_RANGE,
} from "../../../pages/PosList/partials/posfilters/partials/FilterVolumeRange/volumeRange";
import buildQueryVariables from "./buildQueryVariables";

interface PosFiltersState {
  filters: PosFilters;
  sort: SortType;
  mapLocation?: LocationWhereInput;
  userLocation?: UserLocationInput;
  filterByMapLocation?: boolean;
  queryVariables: any;
}

const initialFilters: PosFilters = {
  search: "",
  isOwner: undefined,
  isActive: undefined,
  underContract: undefined,
  brandIds: [],
  laLiga: undefined,
  lastVisitDate: undefined,
  missingProducts: [],
  existingProducts: [],
  rentabilibar: undefined,
  segments: [],
  segmentTypes: [],
  postalCodes: [],
  volumeRange: [MIN_VOLUME_RANGE, MAX_VOLUME_RANGE],
};

export const initialState: PosFiltersState = {
  filters: initialFilters,
  sort: null,
  userLocation: undefined,
  // Seeded from the same builder useFilterVariables uses, so the first render
  // and the hook's mount effect produce equal variables and Apollo issues one
  // request instead of two. See buildQueryVariables for what drifted before.
  queryVariables: buildQueryVariables(initialFilters),
};

const slice = createSlice({
  name: "posFilters",
  initialState,
  reducers: {
    SetSearch(state, { payload: { search } }: PayloadAction<SetSearchParams>) {
      state.filters.search = search;
    },
    SetIsOwner(
      state,
      { payload: { isOwner } }: PayloadAction<SetIsOwnerParams>,
    ) {
      state.filters.isOwner = isOwner;
    },
    SetUserLocation(
      state,
      {
        payload: { userLocation, sortBy },
      }: PayloadAction<SetUserLocationParams>,
    ) {
      state.userLocation = userLocation;
      state.sort = sortBy;
    },
    SetMapLocation(
      state,
      { payload: { location } }: PayloadAction<SetMapLocationParams>,
    ) {
      state.mapLocation = location;
    },
    SetLastVisitDate(
      state,
      { payload: { lastVisitDate } }: PayloadAction<SetLastVisitDateParams>,
    ) {
      state.filters.lastVisitDate = lastVisitDate;
    },
    setFilterByMapLocation(
      state,
      {
        payload: { filterByMapLocation },
      }: PayloadAction<{ filterByMapLocation: boolean }>,
    ) {
      state.filterByMapLocation = filterByMapLocation;
    },
    SetBrandIds(
      state,
      { payload: { brandIds } }: PayloadAction<SetBrandIdsParams>,
    ) {
      state.filters.brandIds = brandIds;
    },
    SetSegmentTypes(
      state,
      { payload: { segmentTypes } }: PayloadAction<SetSegmentTypesParams>,
    ) {
      state.filters.segmentTypes = segmentTypes;
    },
    SetSegments(
      state,
      { payload: { segments } }: PayloadAction<SetSegmentsParams>,
    ) {
      state.filters.segments = segments;
    },
    SetClientType(
      state,
      { payload: { clientType } }: PayloadAction<SetClientTypeParams>,
    ) {
      state.filters.clientType = clientType;
    },
    SetUnderContract(
      state,
      { payload: { underContract } }: PayloadAction<SetUnderContractParams>,
    ) {
      state.filters.underContract = underContract;
    },
    SetLaLiga(state, { payload: { laLiga } }: PayloadAction<SetLaLigaParams>) {
      state.filters.laLiga = laLiga;
    },
    SetRentabilibar(
      state,
      { payload: { rentabilibar } }: PayloadAction<SetRentabilibarParams>,
    ) {
      state.filters.rentabilibar = rentabilibar;
    },
    SetActivityStatus(
      state,
      { payload: { isActive } }: PayloadAction<SetActicityStatusParams>,
    ) {
      state.filters.isActive = isActive;
    },
    SetMissingProducts(
      state,
      { payload: { missingProducts } }: PayloadAction<SetMissingProductsParams>,
    ) {
      state.filters.missingProducts = missingProducts;
    },
    SetExistingProducts(
      state,
      {
        payload: { existingProducts },
      }: PayloadAction<SetExistingProductsParams>,
    ) {
      state.filters.existingProducts = existingProducts;
    },
    SetPostalCodes(
      state,
      { payload: { postalCodes } }: PayloadAction<{ postalCodes?: string[] }>,
    ) {
      state.filters.postalCodes = postalCodes;
    },
    SetVolumeRange(
      state,
      {
        payload: { volumeRange },
      }: PayloadAction<{ volumeRange?: [number, number] }>,
    ) {
      state.filters.volumeRange = volumeRange;
    },
    SetQueryVariables(
      state,
      { payload: { variables } }: PayloadAction<SetQueryVariablesParams>,
    ) {
      state.queryVariables = variables;
    },
    ResetPosFiltersState(
      state,
      { payload: { isOwner } }: PayloadAction<SetIsOwnerParams>,
    ) {
      const updatedInitialState = {
        ...initialState,
        mapLocation: state.mapLocation,
        filters: { ...initialState.filters, isOwner },
      };
      return updatedInitialState;
    },
  },
});

export const {
  SetSearch,
  SetIsOwner,
  SetBrandIds,
  SetLaLiga,
  SetRentabilibar,
  SetActivityStatus,
  SetSegmentTypes,
  SetSegments,
  SetClientType,
  SetUnderContract,
  SetMissingProducts,
  SetExistingProducts,
  SetLastVisitDate,
  SetQueryVariables,
  SetUserLocation,
  ResetPosFiltersState,
  SetMapLocation,
  setFilterByMapLocation,
  SetPostalCodes,
  SetVolumeRange,
} = slice.actions;
export default slice.reducer;
